<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRouter } from 'vue-router';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import BillingCard from './components/BillingCard.vue';
import BillingHeader from './components/BillingHeader.vue';
import DetailItem from './components/DetailItem.vue';
import UsageMetrics from './components/UsageMetrics.vue';
import AddonsSection from './components/AddonsSection.vue';
import PaymentMethodSelector from './components/PaymentMethodSelector.vue';
import PaymentPending from './components/PaymentPending.vue';
import BillingDetailsForm from './components/BillingDetailsForm.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';
import Modal from 'dashboard/components/Modal.vue';

const { accountId } = useAccount();
const store = useStore();
const router = useRouter();

// Getters from billing store
const subscription = useMapGetter('billing/getSubscription');
const accountAddons = useMapGetter('billing/getAddons');
const limits = useMapGetter('billing/getLimits');
const usage = useMapGetter('billing/getUsage');
const availableAddons = useMapGetter('billing/getAvailableAddons');
const supportInfo = useMapGetter('billing/getSupportInfo');
const paymentMethods = useMapGetter('billing/getPaymentMethods');
const pendingPayment = useMapGetter('billing/getPendingPayment');
const uiFlags = useMapGetter('billing/getUIFlags');

// Payment method selection state
const selectedAddon = ref(null);
const showPaymentModal = ref(false);
const selectedPaymentMethod = ref(null);
const showPendingPayment = computed(() => !!pendingPayment.value);

// Computed properties
const planName = computed(() => subscription.value?.plan?.name || '—');
const billingCycle = computed(() => {
  const cycle = subscription.value?.billing_cycle;
  return cycle === 'yearly' ? 'Anual' : 'Mensal';
});

const subscriptionStatus = computed(() => {
  const status = subscription.value?.status;
  const statusMap = {
    active: { label: 'Ativo', class: 'bg-n-teal-9 text-white' },
    trialing: { label: 'Trial', class: 'bg-n-blue-9 text-white' },
    past_due: { label: 'Atrasado', class: 'bg-n-amber-9 text-white' },
    cancelled: { label: 'Cancelado', class: 'bg-n-slate-9 text-white' },
    paused: { label: 'Pausado', class: 'bg-n-purple-9 text-white' },
  };
  return statusMap[status] || { label: status || '—', class: 'bg-n-slate-5' };
});

const subscriptionRenewsOn = computed(() => {
  const endDate = subscription.value?.current_period_end;
  if (!endDate) return '—';
  return format(new Date(endDate), "dd 'de' MMM, yyyy", { locale: ptBR });
});

const trialEndsAt = computed(() => {
  const endDate = subscription.value?.trial_ends_at;
  if (!endDate) return null;
  return format(new Date(endDate), "dd 'de' MMM, yyyy", { locale: ptBR });
});

const trialDaysRemaining = computed(
  () => subscription.value?.trial_days_remaining || 0
);
const isTrialing = computed(() => subscription.value?.in_trial || false);
const effectivePrice = computed(() => {
  const price = subscription.value?.effective_price;
  if (!price || price === 0) return 'Grátis';
  return `R$ ${parseFloat(price).toFixed(2).replace('.', ',')}`;
});

const hasSubscription = computed(() => !!subscription.value);

// Actions
const fetchBillingData = async () => {
  await Promise.all([
    store.dispatch('billing/fetchBillingInfo'),
    store.dispatch('billing/fetchAvailableAddons'),
    store.dispatch('billing/fetchSupportInfo'),
    store.dispatch('billing/fetchPaymentMethods'),
  ]);
};

const onClickBillingPortal = () => {
  const returnUrl = `${window.location.origin}/app/accounts/${accountId.value}/settings/billing`;
  store.dispatch('billing/openPortal', returnUrl);
};

const onClickViewPricing = () => {
  router.push({
    name: 'billing_settings_pricing',
    params: { accountId: accountId.value },
  });
};

const onContactSupport = () => {
  const whatsapp = supportInfo.value?.whatsapp;
  const email = supportInfo.value?.email;

  // Priority: WhatsApp > Chatwoot Widget > Email
  if (whatsapp) {
    // Open WhatsApp with the support number
    const cleanNumber = whatsapp.replace(/\D/g, '');
    const message = encodeURIComponent(
      'Olá! Preciso de ajuda com minha assinatura.'
    );
    window.open(`https://wa.me/${cleanNumber}?text=${message}`, '_blank');
  } else if (window.$chatwoot) {
    // Try Chatwoot widget
    window.$chatwoot.toggle();
  } else if (email) {
    // Fallback to email
    window.location.href = `mailto:${email}?subject=Suporte - Assinatura`;
  }
};

const handleAddAddon = async addon => {
  selectedAddon.value = addon;

  // If addon is free, just add it
  if (!addon.monthly_price || addon.monthly_price === 0) {
    await processAddonCheckout('stripe');
    return;
  }

  // Ensure payment methods are loaded
  if (paymentMethods.value.length === 0) {
    await store.dispatch('billing/fetchPaymentMethods');
  }

  // If multiple payment methods are available, show selector
  if (paymentMethods.value.length > 1) {
    selectedPaymentMethod.value = paymentMethods.value[0]?.id || null;
    showPaymentModal.value = true;
    return;
  }

  // Otherwise, proceed with default payment method
  await processAddonCheckout(paymentMethods.value[0]?.id || 'stripe');
};

const handlePaymentMethodSelect = async methodId => {
  selectedPaymentMethod.value = methodId;
  showPaymentModal.value = false;
  await processAddonCheckout(methodId);
};

const processAddonCheckout = async paymentMethod => {
  await store.dispatch('billing/addAddon', {
    addonId: selectedAddon.value.id,
    quantity: 1,
    billingCycle: subscription.value?.billing_cycle || 'monthly',
    paymentMethod,
  });
};

const closePendingPayment = () => {
  store.dispatch('billing/clearPendingPayment');
};

const refreshPaymentStatus = async () => {
  // Refresh billing info to check if payment was confirmed
  await store.dispatch('billing/fetchBillingInfo');
  await store.dispatch('billing/fetchAvailableAddons');

  // If the addon was added successfully, close the modal
  if (pendingPayment.value?.addonId) {
    const addonAdded = accountAddons.value.some(
      a => a.addon?.id === pendingPayment.value.addonId && a.status === 'active'
    );
    if (addonAdded) {
      store.dispatch('billing/clearPendingPayment');
    }
  }
};

const handleRemoveAddon = async accountAddonId => {
  await store.dispatch('billing/removeAddon', accountAddonId);
};

onMounted(fetchBillingData);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('BILLING_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
        feature-name="billing"
      >
        <template #actions>
          <div class="flex gap-2">
            <ButtonV4 sm faded slate @click="onClickViewPricing">
              {{ $t('BILLING_SETTINGS.VIEW_PRICING') }}
            </ButtonV4>
            <ButtonV4
              v-if="subscription?.stripe_linked || subscription?.asaas_linked"
              sm
              solid
              blue
              :is-loading="uiFlags.isCheckoutInProcess"
              @click="onClickBillingPortal"
            >
              {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.BUTTON_TXT') }}
            </ButtonV4>
          </div>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <section class="grid gap-6">
        <!-- Subscription Card -->
        <BillingCard
          :title="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.TITLE')"
          :description="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION')"
        >
          <div
            v-if="hasSubscription"
            class="grid lg:grid-cols-5 md:grid-cols-3 grid-cols-2 gap-4 divide-x divide-n-weak px-5"
          >
            <DetailItem
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.TITLE')"
              :value="planName"
            />
            <DetailItem :label="$t('BILLING_SETTINGS.CURRENT_PLAN.STATUS')">
              <template #value>
                <span
                  class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="subscriptionStatus.class"
                >
                  {{ subscriptionStatus.label }}
                </span>
              </template>
            </DetailItem>
            <DetailItem
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.BILLING_CYCLE')"
              :value="billingCycle"
            />
            <DetailItem
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.PRICE')"
              :value="effectivePrice"
            />
            <DetailItem
              v-if="isTrialing"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.TRIAL_ENDS')"
            >
              <template #value>
                <span class="text-n-amber-11 font-semibold">
                  {{ trialEndsAt }} ({{ trialDaysRemaining }}
                  {{ $t('BILLING_SETTINGS.CURRENT_PLAN.DAYS') }})
                </span>
              </template>
            </DetailItem>
            <DetailItem
              v-else
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.RENEWS_ON')"
              :value="subscriptionRenewsOn"
            />
          </div>

          <!-- Trial warning banner -->
          <div
            v-if="isTrialing && trialDaysRemaining <= 7"
            class="mx-5 mt-4 p-4 rounded-lg bg-n-amber-2 border border-n-amber-6"
          >
            <div class="flex items-start gap-3">
              <span
                class="i-lucide-alert-triangle text-n-amber-11 text-xl flex-shrink-0"
              />
              <div>
                <p class="text-sm font-medium text-n-amber-12">
                  {{
                    $t('BILLING_SETTINGS.TRIAL_WARNING.TITLE', {
                      days: trialDaysRemaining,
                    })
                  }}
                </p>
                <p class="text-sm text-n-amber-11 mt-1">
                  {{ $t('BILLING_SETTINGS.TRIAL_WARNING.DESCRIPTION') }}
                </p>
                <ButtonV4
                  xs
                  solid
                  amber
                  class="mt-3"
                  @click="onClickViewPricing"
                >
                  {{ $t('BILLING_SETTINGS.TRIAL_WARNING.UPGRADE_NOW') }}
                </ButtonV4>
              </div>
            </div>
          </div>
        </BillingCard>

        <!-- Usage Metrics Card -->
        <BillingCard
          :title="$t('BILLING_SETTINGS.USAGE.TITLE')"
          :description="$t('BILLING_SETTINGS.USAGE.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4 xs faded slate @click="onClickViewPricing">
              {{ $t('BILLING_SETTINGS.USAGE.UPGRADE') }}
            </ButtonV4>
          </template>
          <div class="px-5">
            <UsageMetrics :limits="limits" :usage="usage" />
          </div>
        </BillingCard>

        <!-- Billing Details (CPF/CNPJ) -->
        <BillingCard
          :title="$t('BILLING_SETTINGS.BILLING_DETAILS.TITLE')"
          :description="$t('BILLING_SETTINGS.BILLING_DETAILS.DESCRIPTION')"
        >
          <div class="px-5">
            <BillingDetailsForm @updated="fetchBillingData" />
          </div>
        </BillingCard>

        <!-- Add-ons Section -->
        <BillingCard
          :title="$t('BILLING_SETTINGS.ADDONS.TITLE')"
          :description="$t('BILLING_SETTINGS.ADDONS.DESCRIPTION')"
        >
          <div class="px-5">
            <AddonsSection
              :available-addons="availableAddons"
              :account-addons="accountAddons"
              :is-loading="uiFlags.isUpdating"
              @add-addon="handleAddAddon"
              @remove-addon="handleRemoveAddon"
            />
          </div>
        </BillingCard>

        <!-- Need Help Section -->
        <BillingHeader
          class="px-1 mt-2"
          :title="$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE')"
          :description="$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION')"
        >
          <ButtonV4
            sm
            faded
            slate
            icon="i-lucide-life-buoy"
            @click="onContactSupport"
          >
            {{ $t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT') }}
          </ButtonV4>
        </BillingHeader>
      </section>

      <!-- Payment Method Selection Modal -->
      <Modal
        :show="showPaymentModal"
        size="small"
        @close="showPaymentModal = false"
      >
        <div class="p-6">
          <PaymentMethodSelector
            :payment-methods="paymentMethods"
            :selected-method="selectedPaymentMethod"
            :is-loading="uiFlags.isUpdating"
            @select="handlePaymentMethodSelect"
            @cancel="showPaymentModal = false"
          />
        </div>
      </Modal>

      <!-- Pending Payment Modal (PIX/Boleto) -->
      <Modal
        :show="showPendingPayment"
        size="small"
        @close="closePendingPayment"
      >
        <div class="p-6">
          <PaymentPending
            v-if="pendingPayment"
            :payment="pendingPayment"
            @close="closePendingPayment"
            @refresh="refreshPaymentStatus"
          />
        </div>
      </Modal>
    </template>
  </SettingsLayout>
</template>
